import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import { setupErrorFiltering } from './utils/errorFilter';
import InstallPrompt from './components/InstallPrompt';
import OfflineBanner from './components/OfflineBanner';

// Import des pages
import LoginPage from './pages/LoginPage';
import Login from './pages/Login';
import DashboardPage from './pages/DashboardPage';
import ModernDashboard from './pages/ModernDashboard';
import MembersPage from './pages/MembersPage';
import ContributionsPage from './pages/ContributionsPage';
import CoupleContributionsPage from './pages/CoupleContributionsPage';
import AllContributionsPage from './pages/AllContributionsPage';
import TestIntegrationPage from './pages/TestIntegrationPage';
import AuthTestPage from './pages/AuthTestPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import GalleryPage from './pages/Gallery';  
import ResourcesPage from './pages/ResourcesPage';  
import EventsPage from './pages/EventsPage';
import AdminEventsPage from './pages/AdminEventsPage';
import AdminEventRegistrations from './pages/AdminEventRegistrations';  
import AdminContacts from './pages/AdminContacts';  
import AdminInscriptions from './pages/AdminInscriptions';
import AdminGallery from './pages/AdminGallery';
import AdminResources from './pages/AdminResources';  
import AdminNewsletter from './pages/AdminNewsletter';
import AdminMailing from './pages/AdminMailing';
import AutoRedirect from './components/AutoRedirect';
import ZoneDashboard from './pages/ZoneDashboard';
import ZoneMembersPage from './pages/ZoneMembersPage';
import FraternityMembersPage from './pages/FraternityMembersPage';
import ZoneEventsPage from './pages/ZoneEventsPage';
import FraternityMembers from './pages/FraternityMembers';
import ZoneContributions from './pages/ZoneContributions';
import DiagnosticZone from './pages/DiagnosticZone';
import ZoneContributionsWorking from './pages/ZoneContributionsWorking';
import ZoneContributionsTest from './pages/ZoneContributionsTest';
import ProfilePage from './pages/ProfilePage';
import MemberDashboard from './pages/MemberDashboard';
import DonsPage from './pages/DonsPage';
import DonationPublicPage from './pages/DonationPublicPage';
import LoveDinnersPage from './pages/LoveDinnersPage';

import './App.css';
import Home from './pages/Home';
import PrayersPage from './pages/PrayersPage';
import DonatePage from './pages/DonatePage';
import LegalMentions from './pages/LegalMentions';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SiteMap from './pages/SiteMap';
import About from './pages/About';


function App() {
  // Configurer le filtrage des erreurs au démarrage de l'application
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    setupErrorFiltering();

    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          {/* Banner de statut hors ligne */}
          <OfflineBanner />
          
          {/* Prompt d'installation PWA */}
          <InstallPrompt />
          
          {showScrollTop && (
            <button
              type="button"
              onClick={scrollToTop}
              aria-label="Retour en haut"
              className="fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-fsf-primary to-fsf-secondary text-white shadow-xl transition-all duration-300 hover:scale-110 hover:shadow-2xl"
            >
              <ArrowUp className="h-5 w-5" />
            </button>
          )}

          <Routes>
            {/* Route publique */}
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/login-test" element={<Login />} />
            <Route path="/diagnostic-zone" element={<DiagnosticZone />} />
            <Route path="/auto-redirect" element={<AutoRedirect />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
            
            {/* Routes protégées du dashboard - nécessitent rôles admin, apz, apd, moderateur */}
            <Route path="/dashboard" element={
              <ProtectedRoute dashboardOnly={true}>
                <ModernDashboard />
              </ProtectedRoute>
            } />
            
            {/* Routes du dashboard de zone - nécessitent rôles apz, sgz, tgz */}
            <Route path="/zone-dashboard" element={
              <ProtectedRoute zoneOnly={true}>
                <ZoneDashboard />
              </ProtectedRoute>
            } />
            
            {/* Route du profil utilisateur - accessible à tous les utilisateurs connectés */}
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            
            {/* Espace personnel du membre (PWA) */}
            <Route path="/member-dashboard" element={
              <ProtectedRoute>
                <MemberDashboard />
              </ProtectedRoute>
            } />

            <Route path="/zone-members" element={
              <ProtectedRoute zoneOnly={true}>
                <ZoneMembersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/fraternity/:fraternityName/members" element={
              <ProtectedRoute zoneOnly={true}>
                <FraternityMembersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/zone-contributions-working-test" element={
              <ProtectedRoute zoneOnly={true}>
                <ZoneContributionsTest />
              </ProtectedRoute>
            } />
            
            <Route path="/zone-contributions-new" element={
              <ProtectedRoute zoneOnly={true}>
                <ZoneContributionsWorking />
              </ProtectedRoute>
            } />
            
            <Route path="/zone-events" element={
              <ProtectedRoute zoneOnly={true}>
                <ZoneEventsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/zone-dashboard/fraternity/:fraternityName/members" element={
              <ProtectedRoute zoneOnly={true}>
                <FraternityMembers />
              </ProtectedRoute>
            } />
            
            <Route path="/zone-contributions" element={
              <ProtectedRoute zoneOnly={true}>
                <ZoneContributionsWorking />
              </ProtectedRoute>
            } />
            
            <Route path="/zone-dashboard/contributions" element={
              <ProtectedRoute zoneOnly={true}>
                <ZoneContributions />
              </ProtectedRoute>
            } />
            
            <Route path="/members" element={
              <ProtectedRoute dashboardOnly={true}>
                <MembersPage />
              </ProtectedRoute>
            } />
            
            <Route path="/contributions" element={
              <ProtectedRoute dashboardOnly={true}>
                <ContributionsPage />
              </ProtectedRoute>
            } />
            
            {/* Route pour voir toutes les contributions (dons + cotisations) */}
            <Route path="/all-contributions" element={
              <ProtectedRoute dashboardOnly={true}>
                <AllContributionsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/test-integration" element={
              <ProtectedRoute dashboardOnly={true}>
                <TestIntegrationPage />
              </ProtectedRoute>
            } />
            
            <Route path="/auth-test" element={
              <ProtectedRoute dashboardOnly={true}>
                <AuthTestPage />
              </ProtectedRoute>
            } />
            
            {/* Administration des événements */}
            <Route path="/admin/events" element={
              <ProtectedRoute dashboardOnly={true}>
                <AdminEventsPage />
              </ProtectedRoute>
            } />
            
            {/* Gestion des inscriptions aux événements */}
            <Route path="/admin/event-registrations" element={
              <ProtectedRoute dashboardOnly={true}>
                <AdminEventRegistrations />
              </ProtectedRoute>
            } />
            
            {/* Gestion des formulaires de contact */}
            <Route path="/admin/contacts" element={
              <ProtectedRoute dashboardOnly={true}>
                <AdminContacts />
              </ProtectedRoute>
            } />
            
            {/* Gestion des demandes d'inscription */}
            <Route path="/admin/inscriptions" element={
              <ProtectedRoute dashboardOnly={true}>
                <AdminInscriptions />
              </ProtectedRoute>
            } />
            
            {/* Gestion de la galerie d'images */}
            <Route path="/admin/gallery" element={
              <ProtectedRoute dashboardOnly={true}>
                <AdminGallery />
              </ProtectedRoute>
            } />
            
            {/* Gestion des ressources */}
            <Route path="/admin/resources" element={
              <ProtectedRoute dashboardOnly={true}>
                <AdminResources />
              </ProtectedRoute>
            } />
            
            <Route path="/admin/newsletter" element={
              <ProtectedRoute dashboardOnly={true}>
                <AdminNewsletter />
              </ProtectedRoute>
            } />
            
            {/* Gestion du mailing */}
            <Route path="/admin/mailing" element={
              <ProtectedRoute dashboardOnly={true}>
                <AdminMailing />
              </ProtectedRoute>
            } />
            
            {/* Gestion des dons */}
            <Route path="/admin/dons" element={
              <ProtectedRoute dashboardOnly={true}>
                <DonsPage />
              </ProtectedRoute>
            } />
            
            {/* Routes publiques */}
            <Route path="/galerie" element={<GalleryPage />} />
            <Route path="/ressources" element={<ResourcesPage />} />
            <Route path="/evenements" element={<EventsPage />} />
            <Route path="/prieres" element={<PrayersPage/>} />
            <Route path="/don" element={<DonatePage/>} />
            <Route path="/don/:token" element={<DonationPublicPage />} />
            <Route path="/7-diners-en-amoureux" element={<LoveDinnersPage />} />
            
            {/* Pages légales et informationnelles */}
            <Route path="/legal-mentions" element={<LegalMentions />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/sitemap" element={<SiteMap />} />
            <Route path="/about" element={<About />} />
            {/* Route pour voir les cotisations d'un couple */}
            <Route path="/cotisations/:coupleId" element={
              <ProtectedRoute dashboardOnly={true}>
                <CoupleContributionsPage />
              </ProtectedRoute>
            } />  
            <Route path="/cotisations" element={
              <ProtectedRoute dashboardOnly={true}>
                <ContributionsPage />
              </ProtectedRoute>
            } />
            
            {/* Route par défaut - redirection vers dashboard pour les admins */}
            <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
            
            {/* Fallback pour les routes non trouvées - redirection vers l'accueil */}
            {/* <Route path="*" element={<Navigate to="/" replace />} /> */}
          </Routes>
        </div>
        {/* Debug component - remove in production */}
      </Router>
    </AuthProvider>
  );
}

export default App;
// AdminContacts